import { toast } from "sonner";
import { API_URL } from "./config";

// API Configuration and Utilities
export const BASE_URL = `${API_URL}`;

export const checkTokenAndRedirect = () => {
  const token = localStorage.getItem("token");
  if (!token) {
    toast.error("Session expired");
    window.location.href = "/";
    return false;
  }
  return true;
};

export const getAuthHeaders = () => {
  if (!checkTokenAndRedirect()) {
    throw new Error("Session expired - redirecting");
  }
  return {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json"
  };
};

export const apiCall = async (url, options, retries = 2) => {
  if (!checkTokenAndRedirect()) {
    return; // Early return if token is invalid
  }

  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, { ...options, headers: getAuthHeaders() });
      
      // Handle all successful responses, including 202 Accepted
      if (response.ok) {
        // For status codes 204 No Content or 205 Reset Content, don't try to parse JSON
        if (response.status === 204 || response.status === 205) {
          return { success: true, status: response.status };
        }
        
        try {
          return await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, still return a successful response
          return { success: true, status: response.status };
        }
      }
      
      throw new Error(`HTTP error ${response.status}`);
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

export const apiMethods = {
  listProjects: () => apiCall(`${BASE_URL}/project/list-projects`, { method: "GET" }),
  createProject: (data) => apiCall(`${BASE_URL}/project/create-project`, { method: "POST", body: JSON.stringify(data) }),
  updateProject: (id, data) => apiCall(`${BASE_URL}/project/update-project/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteProject: (id) => apiCall(`${BASE_URL}/project/delete-project/${id}`, { method: "DELETE" }),
  checkGithubAccess: () => apiCall(`${BASE_URL}/github/check-repo-access`, { method: "GET" }),
  listGithubRepos: () => apiCall(`${BASE_URL}/github/repositories`, { method: "GET" }),
  getProject: (id) => apiCall(`${BASE_URL}/project/get-project/${id}`, { method: "GET" }),
  listRepositories: (projectId) => apiCall(`${BASE_URL}/repositories/list-repositories?project_id=${projectId}`, { method: "GET" }),
  importPublicRepo: (data) => apiCall(`${BASE_URL}/github/import-public-repository`, { method: "POST", body: JSON.stringify(data) }),
  importPrivateRepo: (data) => apiCall(`${BASE_URL}/github/import-repository`, { method: "POST", body: JSON.stringify(data) }),
  createRepository: (data) => apiCall(`${BASE_URL}/repositories/create-repository`, { method: "POST", body: JSON.stringify(data) }),
  updateRepository: (id, data) => apiCall(`${BASE_URL}/repositories/update-repository/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteRepository: (id) => apiCall(`${BASE_URL}/repositories/delete-repository/${id}`, { method: "DELETE" }),
  uploadLocalFiles: (id,name,data) => apiCall(`${BASE_URL}/storage/upload-from-local?project_id=${id}&name=${name}`, { method: "POST", body: JSON.stringify(data)}),
  getRepository: (repoId) => apiCall(`${BASE_URL}/repositories/get-repository/${repoId}`, { method: "GET" }),
  listRepofiles: (repo_id) => apiCall(`${BASE_URL}/storage/list-files/${repo_id}`, { method: 'GET' }),
  generateDocumentation: (repository_id) => 
    apiCall(`${BASE_URL}/gemini/generate/combined`, { 
      method: "POST", 
      headers: { "Content-Type": "application/json" }, 
      body: JSON.stringify({ repository_id }) 
    }),
  checkGenerationStatus: (repository_id) => 
    apiCall(`${BASE_URL}/gemini/generate/status`, { 
      method: "POST", 
      headers: { "Content-Type": "application/json" }, 
      body: JSON.stringify({ repository_id }) 
    }),
  downloadDocumentation: (repository_id, documentation_id) => 
    apiCall(`${BASE_URL}/documentation/download/${repository_id}/${documentation_id}`, { 
      method: "GET" 
    }),
  downloadArchitecture: (repository_id, documentation_id) => 
    apiCall(`${BASE_URL}/documentation/download/${repository_id}/${documentation_id}?type=architecture`, { 
      method: "GET" 
    }),
  getLatestDocumentation: (repository_id) => 
    apiCall(`${BASE_URL}/documentation/latest/${repository_id}`, { 
      method: "GET" 
    }),
  
  // Notion integration methods
  notionAuthorize: () => apiCall(`${BASE_URL}/integrations/notion/authorize`, { method: "GET" }),
  notionCheckToken: () => apiCall(`${BASE_URL}/integrations/notion/check-token`, { method: "GET" }),
  notionUpload: (repo_id, content) => {
    // Use XMLHttpRequest which shows up immediately in network tab
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Set up request
      xhr.open('POST', `${BASE_URL}/integrations/notion/upload`, true);
      
      // Add headers
      const headers = getAuthHeaders();
      Object.keys(headers).forEach(key => {
        xhr.setRequestHeader(key, headers[key]);
      });
      
      // Handle response
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            // If response isn't JSON, still resolve
            resolve({ success: true, status: xhr.status });
          }
        } else {
          reject(new Error(`HTTP error ${xhr.status}`));
        }
      };
      
      // Handle network errors
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      // Send the request with JSON data
      xhr.send(JSON.stringify({ repo_id, content }));
    });
  },
  notionFetch: (page_id) => 
    apiCall(`${BASE_URL}/integrations/notion/fetch?page_id=${page_id}`, { 
      method: "GET" 
    }),
  notionListPages: (workspace_id) => 
    apiCall(`${BASE_URL}/integrations/notion/pages${workspace_id ? `?workspace_id=${workspace_id}` : ''}`, { 
      method: "GET" 
    }),
  notionCheckConnection: () => apiCall(`${BASE_URL}/integrations/notion/status`, { method: "GET" }),
  notionDisconnect: () => apiCall(`${BASE_URL}/integrations/notion/disconnect`, { method: "POST" }),
  notionExportDocumentation: (repository_id, documentation_id, workspace_id) => 
    apiCall(`${BASE_URL}/integrations/notion/export`, { 
      method: "POST",
      body: JSON.stringify({ repository_id, documentation_id, workspace_id })
    }),
  getNotionConnectedWorkspaces: () => apiCall(`${BASE_URL}/integrations/notion/workspaces`, { method: "GET" }),
};
  
export const authGithubApi = `${BASE_URL}/github/authorize-app`;
export const cache = new Map();

